import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Warning, CheckCircle } from '@phosphor-icons/react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function PacientesSemNutricionistaPage() {
  useRequireAdmin();
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/api/admin/patients-unassigned`),
        axios.get(`${API}/api/staff`),
      ]);
      setPatients(pRes.data || []);
      setStaff(sRes.data || []);
    } catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const assign = async (patientId) => {
    const nutriId = picked[patientId];
    if (!nutriId) { toast.error('Escolhe uma nutricionista'); return; }
    setSaving(patientId);
    try {
      await axios.put(`${API}/api/admin/patients-unassigned/${patientId}/assign`, { nutritionist_id: nutriId });
      toast.success('Nutricionista atribuída');
      setPatients(prev => prev.filter(p => p.user_id !== patientId));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(''); }
  };

  const dismiss = async (patientId) => {
    try {
      await axios.put(`${API}/api/admin/patients-unassigned/${patientId}/dismiss`);
      toast.success('Alerta descartado');
      setPatients(prev => prev.filter(p => p.user_id !== patientId));
    } catch { toast.error('Erro'); }
  };

  const fmtDate = (d) => d ? fmtStamp(d) : '-';

  return (
    <div className="fade-in" data-testid="pacientes-sem-nutri-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl bg-red-100 flex items-center justify-center"><Warning size={22} className="text-red-600" weight="duotone" /></div>
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Pacientes Sem Nutricionista</h2>
          <p className="text-sm text-brand-muted mt-0.5">Autorregisto (email ou Google) ainda sem uma nutricionista atribuída - atribui para começarem o acompanhamento.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-12 text-brand-muted">A carregar...</p>
      ) : patients.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-brand-border rounded-2xl mt-6" data-testid="no-unassigned-patients">
          <CheckCircle size={48} className="mx-auto text-brand-green mb-3" weight="duotone" />
          <p className="text-brand-muted">Sem pacientes pendentes de atribuição.</p>
        </div>
      ) : (
        <div className="space-y-3 mt-6">
          {patients.map(p => (
            <div key={p.user_id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid={`unassigned-patient-${p.user_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-brand-text">{p.name || p.email}</p>
                <p className="text-[11px] text-brand-muted">{p.email}{p.phone && ` · ${p.phone}`} · Registada em {fmtDate(p.created_at)}</p>
              </div>
              <Select value={picked[p.user_id] || ''} onValueChange={v => setPicked(prev => ({ ...prev, [p.user_id]: v }))}>
                <SelectTrigger className="w-52" data-testid={`assign-select-${p.user_id}`}><SelectValue placeholder="Atribuir a..." /></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => assign(p.user_id)} disabled={saving === p.user_id} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-60" data-testid={`assign-btn-${p.user_id}`}>
                {saving === p.user_id ? 'A atribuir…' : 'Atribuir'}
              </button>
              <button onClick={() => dismiss(p.user_id)} className="text-xs text-brand-muted hover:text-red-500" data-testid={`dismiss-btn-${p.user_id}`}>Descartar</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
